import { useCallback, useEffect, useRef, useState } from "react";

import { AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { useLazyGeSuggestionQuery } from "@/pages/Private/redux/liPeople/liPeople.api";
import { useSearch } from "@/utils/useSearch";

import { Suggestions } from "../../LiPeople/schema/liPeople";

/** What the API answered for one keyword: the places it offered, or that the search failed. */
export interface LocationAnswer {
	keyword: string;
	options: AutoCompleteItem[];
	failed: boolean;
}

/**
 * A suggestion request's result reduced to an answer. A reply without a suggestions list is a
 * failed search too: until PREC-4539 the API answered a failed provider call with an empty 200.
 */
export const toLocationAnswer = (
	keyword: string,
	result: { data?: Suggestions | null; isError?: boolean }
): LocationAnswer => {
	const suggestions = result.data?.suggestions;

	if (result.isError || !Array.isArray(suggestions)) {
		return { keyword, options: [], failed: true };
	}

	return {
		keyword,
		options: suggestions.map(({ urn, title }) => ({ id: urn, title })),
		failed: false,
	};
};

/**
 * The Jobs source's Location typeahead (PREC-4539).
 *
 * - A failed search is reported as failed (`failed`), never as an empty list: the field said
 *   "No results" for "Germany" while the provider was refusing every call.
 * - Until the answer for the text in the field has arrived, the field is `isLoading`, so it does
 *   not say "No results" while it is still asking.
 * - An answer counts only for the keyword it was asked for, and one that arrives after a newer
 *   question was asked is dropped (apps/web/CLAUDE.md §23).
 * - A failure does not stick: the next edit takes it back, and the same text is asked again (the
 *   debounced query only fires on a change, so a failure resets it, unless a newer text has
 *   already taken its place).
 *
 * `options` is the latest answer's, kept while a newer one loads, so typing an option the user has
 * seen in full still picks it (the field's existing affordance). A failed search clears them: a
 * title from an earlier list the field no longer shows must not be pickable by typing it. What the
 * list shows is decided by `isLoading` and `failed`. The options are LinkedIn's matches for the
 * keyword and are shown as they come — "Köln" answers "Cologne, …", which a text filter on the
 * typed word would hide. An empty field offers nothing: the debounced query keeps the old text for
 * up to its delay after the field is cleared, so its answer can still land then.
 */
export const useLocationSuggestions = () => {
	const [fetchSuggestions] = useLazyGeSuggestionQuery();
	const { query, setQuery, searchValue, setSearchValue } = useSearch();
	const [answer, setAnswer] = useState<LocationAnswer | null>(null);
	const askedForRef = useRef("");

	useEffect(() => {
		askedForRef.current = query;

		if (!query) {
			return;
		}

		fetchSuggestions({ type: "location", keyword: query }).then((result) => {
			if (askedForRef.current !== query) {
				return;
			}

			const next = toLocationAnswer(query, result);

			setAnswer(next);

			if (next.failed) {
				setQuery((current) => (current === query ? "" : current));
			}
		});
	}, [query, setQuery, fetchSuggestions]);

	const handleSearch = useCallback(
		(value: string) => {
			setAnswer((current) => (current?.failed ? null : current));
			setSearchValue(value);
		},
		[setSearchValue]
	);

	const answered = searchValue !== "" && answer?.keyword === searchValue;

	return {
		options: searchValue === "" ? [] : (answer?.options ?? []),
		isLoading: searchValue !== "" && !answered,
		failed: answered && !!answer?.failed,
		setSearchValue: handleSearch,
	};
};
